/**
 * Create a grupo.
 *
 * `scope_id` IS REQUIRED AND HAS NO DEFAULT
 * -----------------------------------------
 * The column has none either, and portal migration 053 argues it at
 * length: a group's purpose is to carry a restriction, so an omitted
 * scope must raise rather than quietly produce one whose members inherit
 * the widest scope. The `Select` therefore opens on an empty option that
 * cannot be saved — never pre-selected on `all`.
 *
 * The group is created EMPTY and granting NOTHING. Both are legal
 * states, both are reported by the list with a word (`no_members`,
 * `grants_nothing`), and the callout says so here so that "I created it
 * and nobody can see anything" is not a surprise.
 */

import { Button, Input, Modal, Select, Textarea, type SelectOption } from "@omnitok/ui";
import { Callout, FormField } from "@pervasivemind/admin-ui";
import { useCallback, useEffect, useState } from "react";

import { useT } from "@/i18n";
import {
  SCOPE_ALL,
  groupsApi,
  scopesApi,
  type ScopeSummaryOut,
} from "@/lib/access-api";

export function GroupCreateModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}): JSX.Element | null {
  const { t } = useT();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [scopeId, setScopeId] = useState("");
  const [scopes, setScopes] = useState<ScopeSummaryOut[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadScopes = useCallback(async (): Promise<void> => {
    try {
      const out = await scopesApi.list();
      // Derived `eff_…` rows are owned by the recompute and must not be
      // assignable by hand: `users.effective_scope_id` would then point
      // at a row a person picked instead of one the recompute owns.
      setScopes(out.scopes.filter((s) => s.scope_kind !== "effective"));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setName("");
    setDescription("");
    setScopeId("");
    setError(null);
    void loadScopes();
  }, [open, loadScopes]);

  if (!open) return null;

  const canSave = name.trim() !== "" && scopeId !== "" && !saving;

  const submit = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const created = await groupsApi.create({
        name: name.trim(),
        description,
        scope_id: scopeId,
      });
      onCreated(created.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const options: SelectOption[] = [
    { value: "", label: t("access.groups.form.scopePlaceholder") },
    ...scopes.map((s) => ({
      value: s.id,
      label:
        s.id === SCOPE_ALL
          ? t("access.groups.form.scopeAllOption")
          : `${s.name} (${s.id})`,
    })),
  ];

  return (
    <Modal
      open
      onClose={onClose}
      title={t("access.groups.form.createTitle")}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button ghost size="sm" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!canSave}
            onClick={() => void submit()}
          >
            {t("common.create")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Callout tone="info" title={t("access.groups.form.emptyTitle")}>
          {t("access.groups.form.empty")}
        </Callout>

        {error !== null && (
          <Callout tone="danger" title={t("access.common.errorTitle")}>
            <span className="whitespace-pre-wrap">{error}</span>
          </Callout>
        )}

        <FormField
          label={t("access.groups.form.name")}
          help={t("access.groups.form.nameHelp")}
          required
        >
          <Input
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setName(e.target.value)
            }
          />
        </FormField>

        <FormField label={t("access.groups.form.description")}>
          <Textarea
            value={description}
            rows={2}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setDescription(e.target.value)
            }
          />
        </FormField>

        <FormField
          label={t("access.groups.form.scope")}
          help={t("access.groups.form.scopeHelp")}
          required
        >
          <Select
            aria-label={t("access.groups.form.scope")}
            value={scopeId}
            options={options}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setScopeId(e.target.value)
            }
          />
        </FormField>

        {scopeId === SCOPE_ALL && (
          <Callout tone="warning" title={t("access.groups.form.scopeAllTitle")}>
            {t("access.groups.form.scopeAllWarn")}
          </Callout>
        )}
      </div>
    </Modal>
  );
}
